import { useEffect, useState } from "react";
import {
  Badge,
  DescriptionList,
  FormModal,
  Select,
  Text,
} from "@marekl/adminer/components";
import { apiErrorMessage, notifyError } from "@marekl/adminer/api";
import { useT } from "@marekl/adminer/hooks";
import {
  useMergeCategories,
  useMergePreview,
  type CompetitionCategory,
  type MergePreview,
} from "../../../lib/queries/startlists";
import { formatWindow } from "../../../lib/startListState";
import styles from "./MergeCategoriesModal.module.css";
import type { ID } from "../../../lib/id";

/**
 * Slučování soutěžních kategorií.
 *
 * A merge changes what competitors may perform, so the dialog states the
 * resolved rules every time — never silently. What it does NOT do is
 * offer a choice that isn't one: the validator already requires the same
 * discipline, formation, floor, deduction table, scoring sheet, judge
 * counts and result formula, so the only thing that can genuinely differ
 * between the members is the stopáž window. That is the single decision
 * on offer, and only when the windows actually differ; otherwise the
 * dialog simply says what will apply.
 *
 * The preview runs the same validation as the merge, so an impossible
 * combination is refused here rather than after the organizer commits.
 */
export function MergeCategoriesModal({
  open,
  competitionId,
  categoryIds,
  onClose,
  onMerged,
}: {
  open: boolean;
  competitionId: ID;
  categoryIds: ID[];
  onClose: () => void;
  onMerged: (row: CompetitionCategory) => void;
}) {
  const t = useT();
  const preview = useMergePreview(competitionId);
  const merge = useMergeCategories(competitionId);

  const [governing, setGoverning] = useState<ID | undefined>(undefined);
  const [resolved, setResolved] = useState<MergePreview | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const { mutate: runPreview, reset: resetPreview } = preview;

  // The ids as a STRING, and the effect below depends on that rather than
  // on the array.
  //
  // The parent builds `categoryIds` with `.map()`, so it is a new array on
  // every one of its renders — and a merge invalidates the categories
  // query, which re-renders the parent while this dialog is still open.
  // The effect therefore fired again with the ids the merge had just
  // deleted, and the organizer got „Soutěžní kategorie nenalezena" on top
  // of a merge that had in fact succeeded.
  const idsKey = categoryIds.join(",");

  // Resolve on open and whenever the choice changes: the name and the
  // window both follow the governing combination, so the dialog must
  // restate them rather than let the organizer confirm a stale summary.
  useEffect(() => {
    const ids = idsKey ? idsKey.split(",") : [];
    if (!open || ids.length < 2) return;
    setFailure(null);
    runPreview(
      { categoryIds: ids, governing },
      {
        onSuccess: (data) => setResolved(data),
        onError: (err) => {
          setResolved(null);
          setFailure(apiErrorMessage(err, t));
        },
      },
    );
  }, [open, idsKey, governing, runPreview, t]);

  useEffect(() => {
    if (open) return;
    setGoverning(undefined);
    setResolved(null);
    setFailure(null);
    resetPreview();
  }, [open, resetPreview]);

  const onConfirm = () =>
    merge.mutate(
      { categoryIds, governing },
      {
        onSuccess: (row) => onMerged(row),
        onError: (err) => notifyError(err),
      },
    );

  const rules = resolved?.members.find((m) => m.governing);
  const window = rules ? formatWindow(rules.time_min_seconds, rules.time_max_seconds) : "";

  return (
    <FormModal
      open={open}
      title="Sloučit soutěžní kategorie"
      submitLabel="Sloučit"
      busy={merge.isPending}
      // Until the server resolves the preview there is nothing to merge, and
      // no governing category to pick.
      canSubmit={!!resolved}
      // Already a string — the dialog passes it through untouched.
      error={failure ?? undefined}
      onSubmit={onConfirm}
      onClose={onClose}
      width="lg"
    >

      {resolved && (
        <div className={styles.stack}>
          <div>
            <Text variant="muted">Vznikne kategorie</Text>
            <div className={styles.name}>{resolved.name}</div>
          </div>

          <div>
            <Text variant="muted">Sloučené kategorie</Text>
            <ul className={styles.members}>
              {resolved.members.map((m) => (
                <li key={m.discipline_category_id}>
                  {m.name}
                  {m.governing && resolved.time_differs && (
                    <>
                      {" "}
                      <Badge variant="info">řídí</Badge>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {resolved.time_differs && (
            <div className={styles.choice}>
              <Select
                label="Podle které kategorie se bude soutěžit?"
                value={String(rules?.discipline_category_id ?? "")}
                onChange={(e) => setGoverning(e.target.value)}
              >
                {resolved.members.map((m) => (
                  // The window belongs in the label: it is the only thing
                  // that differs between the options, so a list of bare
                  // category names would be a choice with no visible
                  // difference between the answers.
                  <option key={m.discipline_category_id} value={m.discipline_category_id}>
                    {m.name} ·{" "}
                    {formatWindow(m.time_min_seconds, m.time_max_seconds) || "bez limitu"}
                  </option>
                ))}
              </Select>
              <Text variant="muted">
                Sloučené kategorie mají různý časový limit. Zvolená platí pro všechny; kdo měl
                delší limit, musí sestavu zkrátit.
              </Text>
            </div>
          )}

          <div>
            <Text variant="muted">Bude se soutěžit podle těchto pravidel</Text>
            <DescriptionList>
              <DescriptionList.Item label="Časový limit">{window || "bez limitu"}</DescriptionList.Item>
              <DescriptionList.Item label="Soutěžní plocha">
                {resolved.floor_name || "—"}
              </DescriptionList.Item>
              <DescriptionList.Item label="Srážková tabulka">
                {resolved.deduction_table_name || "—"}
              </DescriptionList.Item>
              <DescriptionList.Item label="Hodnoticí tabulka">
                {resolved.scoring_sheet_name || "—"}
              </DescriptionList.Item>
              <DescriptionList.Item label="Porota">
                {resolved.plus_judges}× plusový, {resolved.tech_judges}× technický
              </DescriptionList.Item>
            </DescriptionList>
          </div>

          <Text variant="muted">
            Sloučená kategorie se hodnotí i vyhlašuje jako jedna. Jde kdykoliv rozdělit zpět.
          </Text>
        </div>
      )}
    </FormModal>
  );
}
